import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { sanitizePositiveInt } from '../utils/validation';
import { log } from '../utils/logger';
import { getSafeErrorMessage } from '../utils/security';
import './AdminUsers.css';

const AdminUsers = () => {
  const { user: currentUser, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showCashPointModal, setShowCashPointModal] = useState(false);
  const [showHeroesModal, setShowHeroesModal] = useState(false);
  const [userHeroes, setUserHeroes] = useState([]);
  const [heroesLoading, setHeroesLoading] = useState(false);
  const [heroesUserId, setHeroesUserId] = useState(null); // Armazenar userId dos heróis
  
  // Estados para modal detalhado do herói
  const [showHeroDetailModal, setShowHeroDetailModal] = useState(false);
  const [selectedHeroDetail, setSelectedHeroDetail] = useState(null);
  const [heroDetailLoading, setHeroDetailLoading] = useState(false);
  const [heroEquippedItems, setHeroEquippedItems] = useState([]);
  const [heroInventoryItems, setHeroInventoryItems] = useState([]);
  const [heroWarehouseItems, setHeroWarehouseItems] = useState([]);
  const [heroEffectiveItems, setHeroEffectiveItems] = useState([]);
  const [heroHenchs, setHeroHenchs] = useState([]);
  const [heroWarehouseHenchs, setHeroWarehouseHenchs] = useState([]);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showAddHenchModal, setShowAddHenchModal] = useState(false);
  const [addItemForm, setAddItemForm] = useState({
    itemIdx: '',
    quantity: 1,
    socketNum: 0,
    location: 'inventory' // 'inventory' ou 'warehouse'
  });
  const [addHenchForm, setAddHenchForm] = useState({
    monsterType: '',
    name: '',
    sex: 0,
    level: 1,
    maxLevel: 26,
    exp: 0,
    location: 'pocket' // 'battle', 'pocket', 'warehouse'
  });
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, blocked
  const [filterAdmin, setFilterAdmin] = useState('all'); // all, admin, user
  const [cashPointAmount, setCashPointAmount] = useState('');
  const [adminForm, setAdminForm] = useState({
    adminLevel: 1
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });
  const [message, setMessage] = useState('');
  const [blockForm, setBlockForm] = useState({
    blockType: 'WEB',
    reason: ''
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setMessage(''); // Limpar mensagens anteriores
      
      const params = {
        page: pagination.page || 1,
        limit: pagination.limit || 20
      };
      
      // Só adicionar search se não estiver vazio
      if (searchTerm && searchTerm.trim() !== '') {
        params.search = searchTerm.trim();
      }
      
      const response = await api.get('/admin/users', { params });
      
      const usersFromApi = response.data.users || [];
      const apiPagination = response.data.pagination || {};
      
      // Aplicar filtros no frontend apenas na lista da página atual (exibição)
      let filteredUsers = usersFromApi;
      
      if (filterStatus !== 'all') {
        if (filterStatus === 'active') {
          filteredUsers = filteredUsers.filter(u => !u.block || u.block === 'ALLOW');
        } else if (filterStatus === 'blocked') {
          filteredUsers = filteredUsers.filter(u => u.block && u.block !== 'ALLOW');
        }
      }
      
      if (filterAdmin !== 'all') {
        if (filterAdmin === 'admin') {
          filteredUsers = filteredUsers.filter(u => u.isAdmin > 0);
        } else if (filterAdmin === 'user') {
          filteredUsers = filteredUsers.filter(u => u.isAdmin === 0);
        }
      }
      
      setUsers(filteredUsers);
      // Usar a paginação do backend para total e totalPages (assim as outras páginas aparecem)
      setPagination(prev => ({
        ...prev,
        total: apiPagination.total ?? prev.total,
        totalPages: Math.max(1, apiPagination.totalPages ?? prev.totalPages)
      }));
    } catch (error) {
      log.error('Erro ao buscar usuários:', error);
      if (error.response?.status === 403) {
        setMessage('Acesso negado. Apenas administradores podem acessar esta página.');
        navigate('/');
      } else if (error.response?.status === 401) {
        setMessage('Sessão expirada. Por favor, faça login novamente.');
        navigate('/admin');
      } else if (error.response?.data?.error) {
        setMessage(`Erro: ${error.response.data.error}`);
      } else if (error.message) {
        setMessage(`Erro: ${error.message}`);
      } else {
        setMessage('Erro ao carregar usuários. Verifique se o servidor está rodando.');
      }
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, filterStatus, filterAdmin, navigate]);

  useEffect(() => {
    if (authLoading) return; // Aguardar autenticação terminar
    
    if (!currentUser) {
      navigate('/admin');
      return;
    }
    fetchUsers();
  }, [currentUser, navigate, fetchUsers, authLoading]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchUsers();
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleViewDetails = async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}`);
      setSelectedUser(response.data);
      setShowDetails(true);
    } catch (error) {
      log.error('Erro ao buscar detalhes do usuário:', error);
      setMessage('Erro ao carregar detalhes do usuário');
    }
  };

  const handleBlockClick = (userId) => {
    // Buscar dados do usuário e abrir modal
    api.get(`/admin/users/${userId}`)
      .then(response => {
        setSelectedUser(response.data);
        // Resetar form para valores padrão ao abrir modal
        setBlockForm({ blockType: 'WEB', reason: '' });
        setShowBlockModal(true);
      })
      .catch(error => {
        log.error('Erro ao buscar usuário:', error);
        setMessage('Erro ao carregar dados do usuário');
      });
  };

  const handleUnblockClick = (userId) => {
    // Buscar dados do usuário e abrir modal
    api.get(`/admin/users/${userId}`)
      .then(response => {
        setSelectedUser(response.data);
        setShowUnblockModal(true);
      })
      .catch(error => {
        log.error('Erro ao buscar usuário:', error);
        setMessage('Erro ao carregar dados do usuário');
      });
  };

  const handleUnblock = async () => {
    if (!selectedUser) return;

    try {
      await api.post('/admin/blocks/unblock', {
        userId: selectedUser.id
      });
      setMessage('Usuário desbloqueado com sucesso!');
      setShowUnblockModal(false);
      fetchUsers();
      // Atualizar dados do usuário selecionado se o modal de detalhes estiver aberto
      if (showDetails) {
        const response = await api.get(`/admin/users/${selectedUser.id}`);
        setSelectedUser(response.data);
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Erro ao desbloquear usuário');
    }
  };

  const handlePromoteAdminClick = (userId) => {
    // Buscar dados do usuário e abrir modal
    api.get(`/admin/users/${userId}`)
      .then(response => {
        setSelectedUser(response.data);
        setAdminForm({ adminLevel: 1 });
        setShowAdminModal(true);
      })
      .catch(error => {
        log.error('Erro ao buscar usuário:', error);
        setMessage('Erro ao carregar dados do usuário');
      });
  };

  const handlePromoteAdmin = async () => {
    if (!selectedUser) return;

    try {
      await api.post('/admin/admins', {
        userId: selectedUser.id,
        adminLevel: adminForm.adminLevel
      });
      setMessage(`Usuário promovido a administrador nível ${adminForm.adminLevel} com sucesso!`);
      setShowAdminModal(false);
      setAdminForm({ adminLevel: 1 });
      fetchUsers();
      // Atualizar dados do usuário selecionado se o modal de detalhes estiver aberto
      if (showDetails) {
        const response = await api.get(`/admin/users/${selectedUser.id}`);
        setSelectedUser(response.data);
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Erro ao promover usuário');
    }
  };

  const handleRemoveAdmin = async (userId) => {
    if (!window.confirm('Tem certeza que deseja remover os privilégios de administrador deste usuário?')) {
      return;
    }

    try {
      await api.delete(`/admin/admins/${userId}`);
      setMessage('Privilégios de administrador removidos com sucesso!');
      fetchUsers();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Erro ao remover administrador');
    }
  };

  const handleBlockFromModal = async () => {
    if (!selectedUser) return;

    try {
      await api.post('/admin/blocks/block', {
        userId: selectedUser.id,
        blockType: blockForm.blockType,
        reason: blockForm.reason
      });
      setMessage('Usuário bloqueado com sucesso!');
      setShowBlockModal(false);
      setBlockForm({ blockType: 'WEB', reason: '' });
      fetchUsers();
      // Atualizar dados do usuário selecionado
      const response = await api.get(`/admin/users/${selectedUser.id}`);
      setSelectedUser(response.data);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Erro ao bloquear usuário');
    }
  };

  const handleUnblockFromModal = async () => {
    if (!selectedUser) return;

    try {
      await api.post('/admin/blocks/unblock', {
        userId: selectedUser.id
      });
      setMessage('Usuário desbloqueado com sucesso!');
      fetchUsers();
      // Atualizar dados do usuário selecionado
      const response = await api.get(`/admin/users/${selectedUser.id}`);
      setSelectedUser(response.data);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Erro ao desbloquear usuário');
    }
  };

  const handleCashPointClick = (userId) => {
    // Buscar dados do usuário e abrir modal
    api.get(`/admin/users/${userId}`)
      .then(response => {
        setSelectedUser(response.data);
        setCashPointAmount('');
        setShowCashPointModal(true);
      })
      .catch(error => {
        log.error('Erro ao buscar usuário:', error);
        setMessage('Erro ao carregar dados do usuário');
      });
  };

  const handleAddCashPoint = async () => {
    if (!selectedUser) return;

    const amount = sanitizePositiveInt(cashPointAmount, 1, 999999999);
    if (amount <= 0) {
      setMessage('Por favor, insira um valor válido maior que zero.');
      return;
    }

    try {
      const response = await api.post(`/admin/users/${selectedUser.id}/cashpoint`, {
        amount
      });
      setMessage(`CashPoint adicionado com sucesso! Novo total: ${response.data.newAmount.toLocaleString('pt-BR')}`);
      setShowCashPointModal(false);
      setCashPointAmount('');
      fetchUsers();
      // Atualizar dados do usuário selecionado
      const userResponse = await api.get(`/admin/users/${selectedUser.id}`);
      setSelectedUser(userResponse.data);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Erro ao adicionar CashPoint');
    }
  };

  const handleViewHeroes = async (userId) => {
    try {
      setHeroesLoading(true);
      setMessage('');
      
      // Validar userId
      const userIdInt = parseInt(userId);
      if (isNaN(userIdInt) || userIdInt <= 0) {
        setMessage('ID do usuário inválido');
        setHeroesLoading(false);
        return;
      }
      
      const response = await api.get(`/admin/characters/user/${userIdInt}`);
      setUserHeroes(response.data.heroes || []);
      setHeroesUserId(userIdInt); // Armazenar userId para uso posterior
      setSelectedUser({
        id: response.data.player.id,
        username: response.data.player.username,
        email: response.data.player.email
      });
      setShowHeroesModal(true);
    } catch (error) {
      log.error('Erro ao buscar heróis:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.details || 'Erro ao carregar heróis do usuário';
      setMessage(errorMessage);
    } finally {
      setHeroesLoading(false);
    }
  };


  const handleAddItem = async () => {
    try {
      if (!selectedHeroDetail || !heroesUserId) {
        setMessage('Herói não selecionado');
        return;
      }

      if (!addItemForm.itemIdx || parseInt(addItemForm.itemIdx) <= 0) {
        setMessage('ID do item é obrigatório');
        return;
      }

      setMessage('');
      const serialStr = String(selectedHeroDetail.serial);

      const response = await api.post(
        `/admin/characters/${heroesUserId}/${serialStr}/items/add`,
        {
          itemIdx: parseInt(addItemForm.itemIdx),
          quantity: parseInt(addItemForm.quantity) || 1,
          socketNum: parseInt(addItemForm.socketNum) || 0,
          location: addItemForm.location
        }
      );

      setMessage(`✅ ${response.data.message}`);
      setShowAddItemModal(false);
      setAddItemForm({ itemIdx: '', quantity: 1, socketNum: 0, location: 'inventory' });

      // Recarregar itens do herói
      await handleViewHeroDetail(selectedHeroDetail);
    } catch (error) {
      log.error('Erro ao adicionar item:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao adicionar item';
      setMessage(`❌ ${errorMessage}`);
    }
  };

  const handleAddHench = async () => {
    try {
      if (!selectedHeroDetail || !heroesUserId) {
        setMessage('Herói não selecionado');
        return;
      }

      if (!addHenchForm.monsterType || parseInt(addHenchForm.monsterType) <= 0) {
        setMessage('Tipo de monstro é obrigatório');
        return;
      }

      if (!addHenchForm.name || addHenchForm.name.trim() === '') {
        setMessage('Nome do hench é obrigatório');
        return;
      }

      setMessage('');
      const serialStr = String(selectedHeroDetail.serial);

      const response = await api.post(
        `/admin/characters/${heroesUserId}/${serialStr}/henchs/add`,
        {
          monsterType: parseInt(addHenchForm.monsterType),
          name: addHenchForm.name.trim(),
          sex: parseInt(addHenchForm.sex) || 0,
          level: parseInt(addHenchForm.level) || 1,
          maxLevel: parseInt(addHenchForm.maxLevel) || 26,
          exp: parseInt(addHenchForm.exp) || 0,
          location: addHenchForm.location
        }
      );

      setMessage(`✅ ${response.data.message}`);
      setShowAddHenchModal(false);
      setAddHenchForm({ monsterType: '', name: '', sex: 0, level: 1, maxLevel: 26, exp: 0, location: 'pocket' });

      // Recarregar henchs do herói
      await handleViewHeroDetail(selectedHeroDetail);
    } catch (error) {
      log.error('Erro ao adicionar hench:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao adicionar hench';
      setMessage(`❌ ${errorMessage}`);
    }
  };

  const handleViewHeroDetail = async (hero) => {
    try {
      setHeroDetailLoading(true);
      setMessage('');
      
      log.info('handleViewHeroDetail', { hasHero: !!hero, hasUser: !!selectedUser });
      
      if (!hero || !hero.serial) {
        setMessage('Dados do herói inválidos');
        setHeroDetailLoading(false);
        return;
      }

      // Prioridade: heroesUserId > selectedUser.id > hero.id
      let userId = heroesUserId || selectedUser?.id || hero.id;
      
      if (!userId) {
        log.error('ID do usuário não encontrado ao carregar herói');
        setMessage('ID do usuário não encontrado. Por favor, feche e abra novamente o modal de heróis.');
        setHeroDetailLoading(false);
        return;
      }
      
      const serialStr = String(hero.serial);
      
      // Buscar itens (equipados e inventário)
      const itemsResponse = await api.get(`/admin/characters/${userId}/${serialStr}/items`);
      log.info('Itens do herói', { total: itemsResponse.data.items?.length || 0 });
      
      // Verificar se o backend já separou corretamente
      let equippedItems = itemsResponse.data.equipped || [];
      let inventoryItems = itemsResponse.data.inventory || [];
      
      // Se o backend não separou ou separou incorretamente, usar o array items
      if (itemsResponse.data.items && itemsResponse.data.items.length > 0) {
        // Verificar se a separação do backend está correta
        const backendEquipped = itemsResponse.data.equipped?.length || 0;
        const backendInventory = itemsResponse.data.inventory?.length || 0;
        const totalFromBackend = backendEquipped + backendInventory;
        const totalItems = itemsResponse.data.items.length;
        
        // Se a soma de equipped + inventory não bate com items, ou se ambos estão vazios, separar manualmente
        if (totalFromBackend !== totalItems || (backendEquipped === 0 && backendInventory === 0)) {
          log.info('Separando itens manualmente');
          
          // Separar itens (tipos de socket do jogo):
          // socket_type = 0 = Inventário, socket_type = 2 = Equipado, socket_type = 3 = Efetivo
          equippedItems = itemsResponse.data.items.filter(item => {
            const socketType = parseInt(item.socketType) || 0;
            return socketType === 2; // Equipados
          });
          
          inventoryItems = itemsResponse.data.items.filter(item => {
            const socketType = parseInt(item.socketType) || 0;
            return socketType === 0; // Inventário
          });
          
          // Verificar se a separação está correta
          if (equippedItems.length + inventoryItems.length !== itemsResponse.data.items.length) {
            log.error('Separação equipped/inventory inconsistente');
          }
        } else {
          // Verificar se a separação do backend está correta
          if (equippedItems.length + inventoryItems.length !== itemsResponse.data.items.length) {
            log.warn('Backend separou incorretamente, corrigindo');
            // Separar manualmente mesmo assim (tipos de socket do jogo)
            // socket_type = 0 = Inventário, socket_type = 2 = Equipado, socket_type = 3 = Efetivo
            equippedItems = itemsResponse.data.items.filter(item => {
              const socketType = parseInt(item.socketType) || 0;
              return socketType === 2; // Equipados
            });
            inventoryItems = itemsResponse.data.items.filter(item => {
              const socketType = parseInt(item.socketType) || 0;
              return socketType === 0; // Inventário
            });
          }
        }
      }
      
      // Buscar henchs
      let henchsResponse = { data: { henchs: [] } };
      try {
        henchsResponse = await api.get(`/admin/characters/${userId}/${serialStr}/henchs`);
      } catch (henchError) {
        log.error('Erro ao buscar henchs', henchError);
      }

      setSelectedHeroDetail(hero);
      setHeroEquippedItems(equippedItems);
      setHeroInventoryItems(inventoryItems);
      setHeroWarehouseItems(itemsResponse.data.warehouse || []);
      setHeroEffectiveItems(itemsResponse.data.effective || []);
      
      // Henchs: backend retorna { henchs: [...], battle: [...], warehouse: [...] }
      // Usar 'henchs' para compatibilidade, ou 'battle' e 'warehouse' se disponíveis
      const allHenchs = henchsResponse.data.henchs || henchsResponse.data.battle || [];
      const warehouseHenchs = henchsResponse.data.warehouse || [];
      setHeroHenchs(allHenchs);
      setHeroWarehouseHenchs(warehouseHenchs);
      setShowHeroDetailModal(true);
    } catch (error) {
      log.error('Erro ao buscar detalhes do herói', error);
      setMessage(getSafeErrorMessage(error, 'Erro ao carregar detalhes do herói'));
    } finally {
      setHeroDetailLoading(false);
    }
  };

  const getHeroTypeName = (type) => {
    const types = {
      0: 'Guerreiro',
      1: 'Mago',
      2: 'Arqueiro',
      3: 'Assassino'
    };
    return types[type] || `Tipo ${type}`;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const getBlockStatus = (block) => {
    // Traduzir status de bloqueio (formato esperado pelo cliente)
    if (!block || block === 'ALLOW') {
      return { text: 'Ativo', class: 'active' };
    }
    if (block === 'GAME') return { text: 'Bloqueado (Jogo)', class: 'blocked-game' };
    if (block === 'WEB') return { text: 'Bloqueado (Web)', class: 'blocked-web' };
    if (block === 'SUM' || block === 'ALL') return { text: 'Bloqueado (Total)', class: 'blocked-all' };
    if (block === 'WAIT') return { text: 'Aguardando Desistência', class: 'blocked-wait' };
    if (block === 'SECEDER') return { text: 'Desistiu/Saiu', class: 'blocked-seceder' };
    return { text: 'Bloqueado', class: 'blocked-other' };
  };

  if (loading && users.length === 0) {
    return <div className="admin-users-page"><div className="loading">Carregando...</div></div>;
  }

  return (
    <div className="admin-users-page">
      <div className="container">
        <div className="admin-header">
          <h1>GERENCIAR USUÁRIOS</h1>
        </div>

        {message && (
          <div className={message.includes('Erro') ? 'error' : 'success'}>
            {message}
          </div>
        )}

        <div className="search-section">
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              placeholder="Buscar por nome de usuário ou email..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
                // fetchUsers será chamado automaticamente pelo useEffect
              }}
              className="filter-select"
            >
              <option value="all">Todos os Status</option>
              <option value="active">Apenas Ativos</option>
              <option value="blocked">Apenas Bloqueados</option>
            </select>
            <select
              value={filterAdmin}
              onChange={(e) => {
                setFilterAdmin(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
                // fetchUsers será chamado automaticamente pelo useEffect
              }}
              className="filter-select"
            >
              <option value="all">Todos</option>
              <option value="admin">Apenas Admins</option>
              <option value="user">Apenas Usuários</option>
            </select>
            <button type="submit" className="btn btn-primary">
              Buscar
            </button>
            {(searchTerm || filterStatus !== 'all' || filterAdmin !== 'all') && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('all');
                  setFilterAdmin('all');
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                Limpar
              </button>
            )}
          </form>
        </div>

        <div className="users-list">
          <div className="list-header">
            <h2>Lista de Usuários</h2>
            <div className="pagination-info">
              Total: {pagination.total} | Página {pagination.page} de {pagination.totalPages}
            </div>
          </div>

          {users.length > 0 ? (
            <>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nome de Usuário</th>
                    <th>Email</th>
                    <th>Data de Registro</th>
                    <th>IsAdmin</th>
                    <th>Status</th>
                    <th>Cash Point</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const blockStatus = getBlockStatus(user.block);
                    return (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>{user.username}</td>
                        <td>{user.email}</td>
                        <td>
                          {user.regDate 
                            ? new Date(user.regDate).toLocaleDateString('pt-BR')
                            : 'N/A'}
                        </td>
                        <td>
                          <span className={`admin-badge ${user.isAdmin > 0 ? 'is-admin' : 'not-admin'}`}>
                            {user.isAdmin > 0 ? `Admin (${user.isAdmin})` : 'Usuário'}
                          </span>
                        </td>
                        <td>
                          <span className={`status ${blockStatus.class}`}>
                            {blockStatus.text}
                          </span>
                        </td>
                        <td>{user.cashPoint.toLocaleString('pt-BR')}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-view"
                              onClick={() => handleViewDetails(user.id)}
                              title="Ver detalhes"
                            >
                              👁️
                            </button>
                            {(!user.block || user.block === 'ALLOW') ? (
                              <button
                                className="btn-block"
                                onClick={() => handleBlockClick(user.id)}
                                title="Bloquear usuário"
                              >
                                🚫
                              </button>
                            ) : (
                              <button
                                className="btn-unblock"
                                onClick={() => handleUnblockClick(user.id)}
                                title="Desbloquear usuário"
                              >
                                ✅
                              </button>
                            )}
                            {user.isAdmin === 0 ? (
                              <button
                                className="btn-promote"
                                onClick={() => handlePromoteAdminClick(user.id)}
                                title="Promover a administrador"
                              >
                                👑
                              </button>
                            ) : user.id !== (currentUser?.id || null) && (
                              <button
                                className="btn-remove-admin"
                                onClick={() => handleRemoveAdmin(user.id)}
                                title="Remover privilégios de admin"
                              >
                                🔻
                              </button>
                            )}
                            <button
                              className="btn-cashpoint"
                              onClick={() => handleCashPointClick(user.id)}
                              title="Adicionar CashPoint"
                            >
                              💰
                            </button>
                            <button
                              className="btn-heroes"
                              onClick={() => handleViewHeroes(user.id)}
                              title="Ver Heróis"
                            >
                              ⚔️
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {pagination.totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="btn-pagination"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Anterior
                  </button>
                  <span className="page-info">
                    Página {pagination.page} de {pagination.totalPages}
                  </span>
                  <button
                    className="btn-pagination"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                  >
                    Próxima
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="no-results">
              {searchTerm ? 'Nenhum usuário encontrado com os critérios de busca.' : 'Nenhum usuário cadastrado.'}
            </p>
          )}
        </div>

        {showDetails && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowDetails(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Detalhes do Usuário</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowDetails(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="detail-row">
                  <strong>ID:</strong>
                  <span>{selectedUser.id}</span>
                </div>
                <div className="detail-row">
                  <strong>Nome de Usuário:</strong>
                  <span>{selectedUser.username}</span>
                </div>
                <div className="detail-row">
                  <strong>Email:</strong>
                  <span>{selectedUser.email}</span>
                </div>
                <div className="detail-row">
                  <strong>Data de Registro:</strong>
                  <span>
                    {selectedUser.regDate 
                      ? new Date(selectedUser.regDate).toLocaleString('pt-BR')
                      : 'N/A'}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>IsAdmin:</strong>
                  <span className={`admin-badge ${selectedUser.isAdmin > 0 ? 'is-admin' : 'not-admin'}`}>
                    {selectedUser.isAdmin > 0 ? `Admin (${selectedUser.isAdmin})` : 'Usuário'}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Status:</strong>
                  <span className={`status ${getBlockStatus(selectedUser.block).class}`}>
                    {getBlockStatus(selectedUser.block).text}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Cash Point:</strong>
                  <span>{selectedUser.cashPoint.toLocaleString('pt-BR')}</span>
                </div>
                {selectedUser.sex !== null && (
                  <div className="detail-row">
                    <strong>Sex:</strong>
                    <span>{selectedUser.sex}</span>
                  </div>
                )}
                {selectedUser.uc !== null && (
                  <div className="detail-row">
                    <strong>UC:</strong>
                    <span>{selectedUser.uc}</span>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <div className="modal-actions">
                  {(!selectedUser.block || selectedUser.block === 'ALLOW') ? (
                    <button
                      className="btn btn-danger"
                      onClick={() => {
                        setShowBlockModal(true);
                      }}
                    >
                      🚫 Bloquear
                    </button>
                  ) : (
                    <button
                      className="btn btn-success"
                      onClick={handleUnblockFromModal}
                    >
                      ✅ Desbloquear
                    </button>
                  )}
                  {selectedUser.isAdmin === 0 ? (
                    <button
                      className="btn btn-warning"
                      onClick={() => {
                        setAdminForm({ adminLevel: 1 });
                        setShowAdminModal(true);
                      }}
                    >
                      👑 Promover a Admin
                    </button>
                  ) : selectedUser.id !== (currentUser?.id || null) && (
                    <button
                      className="btn btn-warning"
                      onClick={() => handleRemoveAdmin(selectedUser.id)}
                    >
                      🔻 Remover Admin
                    </button>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setCashPointAmount('');
                      setShowCashPointModal(true);
                    }}
                  >
                    💰 Adicionar CashPoint
                  </button>
                  <button
                    className="btn btn-info"
                    onClick={() => handleViewHeroes(selectedUser.id)}
                  >
                    ⚔️ Ver Heróis
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowDetails(false)}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Bloqueio */}
        {showBlockModal && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowBlockModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Bloquear Usuário</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowBlockModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p><strong>Usuário:</strong> {selectedUser.username} (ID: {selectedUser.id})</p>
                <div className="form-group">
                  <label>Tipo de Bloqueio:</label>
                  <select
                    value={blockForm.blockType}
                    onChange={(e) => setBlockForm(prev => ({ ...prev, blockType: e.target.value }))}
                  >
                    <option value="WEB">Web (Acesso ao site)</option>
                    <option value="GAME">Jogo (Acesso ao jogo)</option>
                    <option value="SUM">Total (Web + Jogo)</option>
                    <option value="WAIT">Aguardando Desistência</option>
                    <option value="SECEDER">Desistiu/Saiu</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Motivo (opcional):</label>
                  <textarea
                    value={blockForm.reason}
                    onChange={(e) => setBlockForm(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Digite o motivo do bloqueio..."
                    rows="3"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-danger"
                  onClick={handleBlockFromModal}
                >
                  Bloquear
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowBlockModal(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Desbloquear */}
        {showUnblockModal && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowUnblockModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Desbloquear Usuário</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowUnblockModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p><strong>Usuário:</strong> {selectedUser.username} (ID: {selectedUser.id})</p>
                <p><strong>Status Atual:</strong> 
                  <span className={`status ${getBlockStatus(selectedUser.block).class}`} style={{ marginLeft: '10px' }}>
                    {getBlockStatus(selectedUser.block).text}
                  </span>
                </p>
                <p style={{ marginTop: '15px', color: '#666' }}>
                  Tem certeza que deseja desbloquear este usuário? Ele terá acesso completo novamente.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-success"
                  onClick={handleUnblock}
                >
                  ✅ Desbloquear
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowUnblockModal(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Promover a Admin */}
        {showAdminModal && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowAdminModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Promover a Administrador</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowAdminModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p><strong>Usuário:</strong> {selectedUser.username} (ID: {selectedUser.id})</p>
                <div className="form-group">
                  <label>Nível de Administrador:</label>
                  <select
                    value={adminForm.adminLevel}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, adminLevel: parseInt(e.target.value) }))}
                  >
                    <option value={1}>1 - Administrador</option>
                    <option value={2}>2 - Super Administrador</option>
                  </select>
                  <p className="form-help-text">
                    <strong>Nível 1:</strong> Acesso ao painel administrativo básico<br />
                    <strong>Nível 2:</strong> Acesso completo (inclui gerenciamento de outros admins)
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-warning"
                  onClick={handlePromoteAdmin}
                >
                  👑 Promover
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowAdminModal(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Adicionar CashPoint */}
        {showCashPointModal && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowCashPointModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Adicionar CashPoint</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowCashPointModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p><strong>Usuário:</strong> {selectedUser.username} (ID: {selectedUser.id})</p>
                <p><strong>CashPoint Atual:</strong> {selectedUser.cashPoint.toLocaleString('pt-BR')}</p>
                <div className="form-group">
                  <label>Quantidade a Adicionar:</label>
                  <input
                    type="number"
                    min="1"
                    value={cashPointAmount}
                    onChange={(e) => setCashPointAmount(e.target.value)}
                    placeholder="Digite a quantidade"
                    className="form-input"
                  />
                </div>
                {cashPointAmount && !isNaN(parseInt(cashPointAmount)) && parseInt(cashPointAmount) > 0 && (
                  <div className="cashpoint-preview">
                    <strong>Novo Total:</strong> {(selectedUser.cashPoint + parseInt(cashPointAmount)).toLocaleString('pt-BR')}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-primary"
                  onClick={handleAddCashPoint}
                  disabled={!cashPointAmount || isNaN(parseInt(cashPointAmount)) || parseInt(cashPointAmount) <= 0}
                >
                  💰 Adicionar
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowCashPointModal(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Heróis do Usuário */}
        {showHeroesModal && selectedUser && (
          <div className="modal-overlay" onClick={() => {
            setShowHeroesModal(false);
          }}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Heróis de {selectedUser.username}</h2>
                <button
                  className="modal-close"
                  onClick={() => {
                    setShowHeroesModal(false);
                  }}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                {heroesLoading ? (
                  <div className="loading">Carregando heróis...</div>
                ) : userHeroes.length > 0 ? (
                  <div className="heroes-list">
                    {userHeroes.map((hero) => (
                      <div key={hero.serial} className="hero-card">
                        <div className="hero-header">
                          <h3>{hero.name}</h3>
                          <span className={`hero-status ${hero.online ? 'online' : 'offline'}`}>
                            {hero.online ? '🟢 Online' : '⚫ Offline'}
                          </span>
                        </div>
                        <div className="hero-info">
                          <div className="hero-info-row">
                            <span><strong>Tipo:</strong> {getHeroTypeName(hero.type)}</span>
                            <span><strong>Nível:</strong> {hero.level}</span>
                            <span><strong>Serial:</strong> {hero.serial}</span>
                          </div>
                          <div className="hero-info-row">
                            <span><strong>Ouro:</strong> {hero.gold.toLocaleString('pt-BR')}</span>
                            <span><strong>EXP:</strong> {hero.exp.toLocaleString('pt-BR')}</span>
                            <span><strong>Pontos Livres:</strong> {hero.freePoints}</span>
                          </div>
                          <div className="hero-stats">
                            <span><strong>STR:</strong> {hero.str}</span>
                            <span><strong>DEX:</strong> {hero.dex}</span>
                            <span><strong>AIM:</strong> {hero.aim}</span>
                            <span><strong>LUCK:</strong> {hero.luck}</span>
                          </div>
                          <button
                            className="btn btn-small btn-primary"
                            onClick={() => handleViewHeroDetail(hero)}
                          >
                            👁️ Ver Herói
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-results">Este usuário não possui heróis.</p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowHeroesModal(false);
                  }}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Detalhado do Herói */}
        {showHeroDetailModal && selectedHeroDetail && selectedUser && (
          <div className="modal-overlay" onClick={() => {
            setShowHeroDetailModal(false);
            setSelectedHeroDetail(null);
            setHeroEquippedItems([]);
            setHeroInventoryItems([]);
            setHeroWarehouseItems([]);
            setHeroEffectiveItems([]);
            setHeroHenchs([]);
            setHeroWarehouseHenchs([]);
          }}>
            <div className="modal-content modal-hero-detail" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{selectedHeroDetail.name} - Detalhes Completos</h2>
                <button
                  className="modal-close"
                  onClick={() => {
                    setShowHeroDetailModal(false);
                    setSelectedHeroDetail(null);
                    setHeroEquippedItems([]);
                    setHeroInventoryItems([]);
                    setHeroWarehouseItems([]);
                    setHeroEffectiveItems([]);
                    setHeroHenchs([]);
                    setHeroWarehouseHenchs([]);
                  }}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                {heroDetailLoading ? (
                  <div className="loading">Carregando detalhes do herói...</div>
                ) : (
                  <>
                    {/* Informações Básicas */}
                    <div className="hero-detail-section">
                      <h3>Informações do Herói</h3>
                      <div className="hero-detail-info">
                        <div><strong>Tipo:</strong> {getHeroTypeName(selectedHeroDetail.type)}</div>
                        <div><strong>Nível:</strong> {selectedHeroDetail.level}</div>
                        <div><strong>Serial:</strong> {selectedHeroDetail.serial}</div>
                        <div><strong>Ouro:</strong> {selectedHeroDetail.gold.toLocaleString('pt-BR')}</div>
                        <div><strong>EXP:</strong> {selectedHeroDetail.exp.toLocaleString('pt-BR')}</div>
                        <div><strong>Status:</strong> {selectedHeroDetail.online ? '🟢 Online' : '⚫ Offline'}</div>
                      </div>
                    </div>

                    {/* Itens Equipados */}
                    <div className="hero-detail-section">
                      <h3>Itens Equipados ({heroEquippedItems.length})</h3>
                      {heroEquippedItems.length > 0 ? (
                        <div className="items-list">
                          {heroEquippedItems.map((item) => (
                            <div key={`equipped-${item.itemIdx}-${item.serial}-${item.socketNum}`} className="item-card">
                              <div className="item-header">
                                <h4>Item ID: {item.itemIdx}</h4>
                                {item.count > 1 && <span className="item-count-badge">x{item.count}</span>}
                              </div>
                              <div className="item-info">
                                <div><strong>Serial:</strong> {item.serial}</div>
                                <div><strong>Socket:</strong> {item.socketType}-{item.socketNum}</div>
                                {item.opt > 0 && <div><strong>OPT:</strong> {item.opt}</div>}
                                {item.optLevel > 0 && <div><strong>OPT Level:</strong> {item.optLevel}</div>}
                                {item.duration > 0 && <div><strong>Duração:</strong> {item.duration}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-items">Nenhum item equipado</p>
                      )}
                    </div>

                    {/* Inventário */}
                    <div className="hero-detail-section">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h3>Inventário ({heroInventoryItems.length})</h3>
                        <button
                          className="btn btn-primary btn-add-item"
                          onClick={() => {
                            setAddItemForm({ ...addItemForm, location: 'inventory' });
                            setShowAddItemModal(true);
                          }}
                        >
                          ➕ Adicionar Item
                        </button>
                      </div>
                      {heroInventoryItems.length > 0 ? (
                        <div className="items-list">
                          {heroInventoryItems.map((item) => (
                            <div key={`inventory-${item.itemIdx}-${item.serial}-${item.socketNum}`} className="item-card">
                              <div className="item-header">
                                <h4>Item ID: {item.itemIdx}</h4>
                                {item.count > 1 && <span className="item-count-badge">x{item.count}</span>}
                              </div>
                              <div className="item-info">
                                <div><strong>Serial:</strong> {item.serial}</div>
                                <div><strong>Socket:</strong> {item.socketType}-{item.socketNum}</div>
                                {item.opt > 0 && <div><strong>OPT:</strong> {item.opt}</div>}
                                {item.optLevel > 0 && <div><strong>OPT Level:</strong> {item.optLevel}</div>}
                                {item.duration > 0 && <div><strong>Duração:</strong> {item.duration}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-items">Inventário vazio</p>
                      )}
                    </div>

                    {/* Armazém */}
                    <div className="hero-detail-section">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h3>Armazém ({heroWarehouseItems.length})</h3>
                        <button
                          className="btn btn-primary btn-add-item"
                          onClick={() => {
                            setAddItemForm({ ...addItemForm, location: 'warehouse' });
                            setShowAddItemModal(true);
                          }}
                        >
                          ➕ Adicionar Item
                        </button>
                      </div>
                      {heroWarehouseItems.length > 0 ? (
                        <div className="items-list">
                          {heroWarehouseItems.map((item) => (
                            <div key={`warehouse-${item.itemIdx}-${item.serial}-${item.socketNum}`} className="item-card">
                              <div className="item-header">
                                <h4>Item ID: {item.itemIdx}</h4>
                                {item.count > 1 && <span className="item-count-badge">x{item.count}</span>}
                              </div>
                              <div className="item-info">
                                <div><strong>Serial:</strong> {item.serial}</div>
                                <div><strong>Socket:</strong> {item.socketType}-{item.socketNum}</div>
                                {item.opt > 0 && <div><strong>OPT:</strong> {item.opt}</div>}
                                {item.optLevel > 0 && <div><strong>OPT Level:</strong> {item.optLevel}</div>}
                                {item.duration > 0 && <div><strong>Duração:</strong> {item.duration}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-items">Armazém vazio</p>
                      )}
                    </div>

                    {/* Itens Efetivos */}
                    <div className="hero-detail-section">
                      <h3>Itens Efetivos ({heroEffectiveItems.length})</h3>
                      {heroEffectiveItems.length > 0 ? (
                        <div className="items-list">
                          {heroEffectiveItems.map((item) => (
                            <div key={`effective-${item.itemIdx}-${item.serial}-${item.socketNum}`} className="item-card">
                              <div className="item-header">
                                <h4>Item ID: {item.itemIdx}</h4>
                                {item.count > 1 && <span className="item-count-badge">x{item.count}</span>}
                              </div>
                              <div className="item-info">
                                <div><strong>Serial:</strong> {item.serial}</div>
                                <div><strong>Socket:</strong> {item.socketType}-{item.socketNum}</div>
                                {item.opt > 0 && <div><strong>OPT:</strong> {item.opt}</div>}
                                {item.optLevel > 0 && <div><strong>OPT Level:</strong> {item.optLevel}</div>}
                                {item.duration > 0 && <div><strong>Duração:</strong> {item.duration}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-items">Nenhum item efetivo</p>
                      )}
                    </div>

                    {/* Henchs */}
                    <div className="hero-detail-section">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h3>Henchs ({heroHenchs.length + heroWarehouseHenchs.length})</h3>
                        <button
                          className="btn btn-primary btn-add-item"
                          onClick={() => setShowAddHenchModal(true)}
                        >
                          ➕ Adicionar Hench
                        </button>
                      </div>
                      {(heroHenchs.length > 0 || heroWarehouseHenchs.length > 0) ? (
                        <div className="henchs-list">
                          {/* Henchs de Batalha/Bolso */}
                          {heroHenchs.map((hench, idx) => (
                            <div key={idx} className="hench-card">
                              <div className="hench-header">
                                <h4>{hench.name || `Hench ${idx + 1}`}</h4>
                                <span className="hench-position">
                                  {hench.position === 0 ? '⚔️ Batalha' : hench.position === 1 ? '👝 Bolso' : '📦 Armazém'}
                                </span>
                              </div>
                              <div className="hench-info">
                                <div><strong>Nível:</strong> {hench.level}/{hench.maxLevel}</div>
                                <div><strong>EXP:</strong> {hench.exp.toLocaleString('pt-BR')}</div>
                                <div><strong>STR:</strong> {hench.str} | <strong>DEX:</strong> {hench.dex} | <strong>AIM:</strong> {hench.aim} | <strong>LUCK:</strong> {hench.luck}</div>
                              </div>
                              {hench.equippedItems && hench.equippedItems.length > 0 && (
                                <div className="hench-items">
                                  <strong>Itens Equipados:</strong>
                                  <div className="items-grid-icon">
                                    {hench.equippedItems.map((item, itemIdx) => (
                                      <div key={itemIdx} className="item-icon" title={`Item ID: ${item.itemIdx}`}>
                                        <div className="item-icon-id">{item.itemIdx}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                          
                          {/* Henchs do Armazém */}
                          {heroWarehouseHenchs.map((hench, idx) => (
                            <div key={`warehouse-hench-${hench.serial}-${idx}`} className="hench-card">
                              <div className="hench-header">
                                <h4>{hench.name || `Hench ${idx + 1}`}</h4>
                                <span className="hench-position">📦 Armazém</span>
                              </div>
                              <div className="hench-info">
                                <div><strong>Nível:</strong> {hench.level}/{hench.maxLevel}</div>
                                <div><strong>EXP:</strong> {hench.exp.toLocaleString('pt-BR')}</div>
                                <div><strong>STR:</strong> {hench.str} | <strong>DEX:</strong> {hench.dex} | <strong>AIM:</strong> {hench.aim} | <strong>LUCK:</strong> {hench.luck}</div>
                              </div>
                              {hench.equippedItems && hench.equippedItems.length > 0 && (
                                <div className="hench-items">
                                  <strong>Itens Equipados:</strong>
                                  <div className="items-grid-icon">
                                    {hench.equippedItems.map((item, itemIdx) => (
                                      <div key={itemIdx} className="item-icon" title={`Item ID: ${item.itemIdx}`}>
                                        <div className="item-icon-id">{item.itemIdx}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-items">Nenhum hench</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal de Adicionar Item */}
        {showAddItemModal && selectedHeroDetail && (
          <div className="modal-overlay" onClick={() => setShowAddItemModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Adicionar Item ao {addItemForm.location === 'warehouse' ? 'Armazém' : 'Inventário'}</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowAddItemModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p><strong>Herói:</strong> {selectedHeroDetail.name} (Serial: {selectedHeroDetail.serial})</p>
                <div className="form-group">
                  <label>ID do Item:</label>
                  <input
                    type="number"
                    min="1"
                    value={addItemForm.itemIdx}
                    onChange={(e) => setAddItemForm({ ...addItemForm, itemIdx: e.target.value })}
                    placeholder="Digite o ID do item"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Quantidade:</label>
                  <input
                    type="number"
                    min="1"
                    value={addItemForm.quantity}
                    onChange={(e) => setAddItemForm({ ...addItemForm, quantity: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Socket Number:</label>
                  <input
                    type="number"
                    min="0"
                    value={addItemForm.socketNum}
                    onChange={(e) => setAddItemForm({ ...addItemForm, socketNum: e.target.value })}
                    className="form-input"
                  />
                  <p className="form-help-text">Número do slot no inventário/armazém</p>
                </div>
                {message && (
                  <div className={message.startsWith('✅') ? 'success' : 'error'}>
                    {message}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-primary"
                  onClick={handleAddItem}
                  disabled={!addItemForm.itemIdx || parseInt(addItemForm.itemIdx) <= 0}
                >
                  ➕ Adicionar
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowAddItemModal(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Adicionar Hench */}
        {showAddHenchModal && selectedHeroDetail && (
          <div className="modal-overlay" onClick={() => setShowAddHenchModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Adicionar Hench</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowAddHenchModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p><strong>Herói:</strong> {selectedHeroDetail.name} (Serial: {selectedHeroDetail.serial})</p>
                <div className="form-group">
                  <label>Tipo de Monstro (Monster Type):</label>
                  <input
                    type="number"
                    min="1"
                    value={addHenchForm.monsterType}
                    onChange={(e) => setAddHenchForm({ ...addHenchForm, monsterType: e.target.value })}
                    placeholder="Digite o tipo de monstro"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Nome:</label>
                  <input
                    type="text"
                    value={addHenchForm.name}
                    onChange={(e) => setAddHenchForm({ ...addHenchForm, name: e.target.value })}
                    placeholder="Digite o nome do hench"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Sexo:</label>
                  <select
                    value={addHenchForm.sex}
                    onChange={(e) => setAddHenchForm({ ...addHenchForm, sex: parseInt(e.target.value) })}
                    className="form-input"
                  >
                    <option value={0}>Masculino</option>
                    <option value={1}>Feminino</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Nível:</label>
                  <input
                    type="number"
                    min="1"
                    value={addHenchForm.level}
                    onChange={(e) => setAddHenchForm({ ...addHenchForm, level: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Nível Máximo:</label>
                  <input
                    type="number"
                    min="1"
                    value={addHenchForm.maxLevel}
                    onChange={(e) => setAddHenchForm({ ...addHenchForm, maxLevel: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>EXP:</label>
                  <input
                    type="number"
                    min="0"
                    value={addHenchForm.exp}
                    onChange={(e) => setAddHenchForm({ ...addHenchForm, exp: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Localização:</label>
                  <select
                    value={addHenchForm.location}
                    onChange={(e) => setAddHenchForm({ ...addHenchForm, location: e.target.value })}
                    className="form-input"
                  >
                    <option value="battle">⚔️ Batalha</option>
                    <option value="pocket">👝 Bolso</option>
                    <option value="warehouse">📦 Armazém</option>
                  </select>
                </div>
                {message && (
                  <div className={message.startsWith('✅') ? 'success' : 'error'}>
                    {message}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-primary"
                  onClick={handleAddHench}
                  disabled={!addHenchForm.monsterType || !addHenchForm.name || addHenchForm.name.trim() === ''}
                >
                  ➕ Adicionar
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowAddHenchModal(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;

